import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BRAND } from '../config';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (activeTab === 'dashboard') loadStats();
  }, [activeTab]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/stats/dashboard', { params: { brand: BRAND } });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des statistiques' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'linear-gradient(135deg, #bf0a30 0%, #3E2723 50%, #5c2e0e 100%)', fontFamily: 'Inter, -apple-system, sans-serif' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <div style={{ color: '#ffffff' }}>Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'linear-gradient(135deg, #bf0a30 0%, #3E2723 50%, #5c2e0e 100%)', fontFamily: 'Inter, -apple-system, sans-serif' }}>
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onLogout={handleLogout}
        user={user}
      />

      <MainContent
        activeTab={activeTab}
        message={message}
        setMessage={setMessage}
      />
    </div>
  );
};

const Sidebar = ({ activeTab, setActiveTab, onLogout, user }) => {
  const navigate = useNavigate();

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'fa-chart-line' },
    { id: 'sessions', label: 'Sessions', icon: 'fa-calendar-alt' },
    { id: 'pdvs', label: 'Points de Vente', icon: 'fa-store' },
    { id: 'stocks', label: 'Stocks', icon: 'fa-boxes', external: true },
    { id: 'daily-quotas', label: 'Quotas Journaliers', icon: 'fa-calendar-day', external: true },
    { id: 'prizes', label: 'Lots', icon: 'fa-gift' },
    { id: 'injection', label: 'Injection', icon: 'fa-syringe' },
    { id: 'participants', label: 'Participants', icon: 'fa-users', external: true },
    { id: 'users', label: 'Utilisateurs', icon: 'fa-user-cog' },
  ];

  const handleMenuClick = (item) => {
    if (item.external) {
      navigate(`/${item.id}`);
    } else {
      setActiveTab(item.id);
    }
  };

  return (
    <div style={{
      width: '250px',
      background: 'rgba(62, 39, 35, 0.95)',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      borderRight: '1px solid rgba(191, 10, 48, 0.3)'
    }}>
      <div style={{
        padding: '24px',
        borderBottom: '1px solid rgba(191, 10, 48, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', justifyContent: 'center' }}>
          <img src="/logo.png" alt="American Cola" style={{ width: '120px', height: '120px' }} />
        </div>
        <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: 0, fontSize: '12px', textAlign: 'center' }}>Administration</p>
      </div>
      <div style={{ padding: '16px', flex: 1 }}>
        {menuItems.map(item => (
          <button
            key={item.id}
            onClick={() => handleMenuClick(item)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: 'none',
              background: activeTab === item.id ? 'rgba(191, 10, 48, 0.3)' : 'transparent',
              textAlign: 'left',
              color: activeTab === item.id ? '#bf0a30' : 'rgba(255, 255, 255, 0.7)',
              cursor: 'pointer',
              borderRadius: '8px',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== item.id) {
                e.target.style.background = 'rgba(191, 10, 48, 0.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== item.id) {
                e.target.style.background = 'transparent';
              }
            }}
          >
            <i className={`fas ${item.icon}`} style={{ width: '20px', textAlign: 'center' }}></i>
            {item.label}
          </button>
        ))}

        {/* Déconnexion placée juste après le menu "Utilisateurs" */}
        <button
          onClick={onLogout}
          style={{
            width: '100%',
            padding: '10px 16px',
            background: 'rgba(255, 255, 255, 0.04)',
            color: '#ff4d4f',
            border: '1px solid rgba(255, 77, 79, 0.7)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '8px'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 77, 79, 0.12)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.04)';
          }}
        >
          <i className="fas fa-sign-out-alt"></i>
          Déconnexion
        </button>
      </div>

      <div style={{ padding: '16px', borderTop: '1px solid rgba(191, 10, 48, 0.3)' }}>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: 600
            }}>
              {user?.username?.charAt(0) || 'A'}
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#ffffff', fontSize: '14px' }}>{user?.username || 'Admin'}</div>
              <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>{user?.role || ''}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MainContent = ({ activeTab, message, setMessage }) => {
  const [stats, setStats] = useState(null);
  const [hourlyData, setHourlyData] = useState(null);
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedDate, setSelectedDate] = useState('all');
  const [selectedPOS, setSelectedPOS] = useState('all');

  const loadStats = async (cityId = null, dateFrom = null, pdvId = null) => {
    try {
      const params = { brand: BRAND };
      if (cityId && cityId !== 'all') params.city_id = parseInt(cityId);
      if (dateFrom && dateFrom !== 'all') params.date_from = dateFrom;
      if (pdvId && pdvId !== 'all') params.pdv_id = pdvId;

      console.log('Loading stats with params:', params);
      const response = await api.get('/admin/stats/dashboard', { params });
      console.log('Stats response:', response.data);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des statistiques' });
    }
  };

  const loadHourlyData = async () => {
    try {
      const params = { brand: BRAND };
      const response = await api.get('/admin/stats/hourly', { params });
      setHourlyData(response.data);
    } catch (error) {
      console.error('Error loading hourly data:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadStats(selectedCity, selectedDate, selectedPOS);
      loadHourlyData();
    }
  }, [activeTab, selectedCity, selectedDate, selectedPOS]);

  if (activeTab === 'sessions') {
    return <SessionsContent message={message} setMessage={setMessage} />;
  }

  if (activeTab === 'pdvs') {
    return <PDVsContent message={message} setMessage={setMessage} />;
  }

  if (activeTab === 'prizes') {
    return <PrizesContent message={message} setMessage={setMessage} />;
  }

  if (activeTab === 'injection') {
    return <InjectionContent message={message} setMessage={setMessage} />;
  }

  if (activeTab === 'users') {
    return <UsersContent message={message} setMessage={setMessage} />;
  }

  if (activeTab !== 'dashboard') {
    return (
      <div style={{ flex: 1, padding: '32px', background: 'transparent' }}>
        <div style={{
          background: 'rgba(62, 39, 35, 0.9)',
          borderRadius: '16px',
          padding: '80px 40px',
          textAlign: 'center',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
          border: '2px solid rgba(191, 10, 48, 0.3)'
        }}>
          <i className="fas fa-tools" style={{ fontSize: '64px', color: '#bf0a30', marginBottom: '24px' }}></i>
          <h3 style={{ color: '#ffffff', marginBottom: '12px', fontSize: '20px', fontWeight: 600 }}>
            Module en développement
          </h3>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', lineHeight: 1.6 }}>
            Ce module sera bientôt disponible.
          </p>
        </div>
      </div>
    );
  }

  const pdvList = stats?.by_pos ? [...new Set(stats.by_pos.map(p => p.name))] : [];

  const getFromDate = (preset) => {
    if (preset === 'all' || !preset) return null;
    const today = new Date();
    if (preset === 'today') {
      return today.toISOString().split('T')[0];
    } else if (preset === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return weekAgo.toISOString().split('T')[0];
    } else if (preset === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      return monthAgo.toISOString().split('T')[0];
    }
    return preset;
  };

  const handleDateChange = (e) => {
    setSelectedDate(getFromDate(e.target.value) || e.target.value);
  };

  return (
    <div style={{ flex: 1, padding: '32px', background: 'transparent', overflowY: 'auto' }}>
      {message.text && (
        <div style={{
          padding: '12px 24px',
          background: message.type === 'error' ? 'rgba(255, 77, 79, 0.15)' : 'rgba(82, 196, 26, 0.15)',
          border: `1px solid ${message.type === 'error' ? 'rgba(255, 77, 79, 0.4)' : 'rgba(82, 196, 26, 0.4)'}`,
          borderRadius: '8px',
          marginBottom: '24px',
          color: message.type === 'error' ? '#ff7875' : '#95de64',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <i className={`fas fa-${message.type === 'error' ? 'exclamation-circle' : 'check-circle'}`}></i>
          {message.text}
        </div>
      )}

      {/* Dashboard Header */}
      <div style={{
        background: 'rgba(62, 39, 35, 0.9)',
        borderRadius: '16px',
        padding: '24px 32px',
        marginBottom: '24px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: '2px solid rgba(191, 10, 48, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="fas fa-bolt" style={{ color: '#ffffff', fontSize: '20px' }}></i>
          </div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#ffffff' }}>
            Dashboard
          </h1>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fas fa-filter" style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}></i>
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              style={{
                padding: '8px 16px',
                border: '1px solid rgba(191, 10, 48, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#ffffff',
                background: 'rgba(62, 39, 35, 0.8)',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="all">Toutes les villes</option>
              {stats?.cities?.map(city => (
                <option key={city.id} value={city.id}>{city.name}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select
              value={typeof selectedDate === 'string' && selectedDate.match(/^\d{4}-\d{2}-\d{2}$/) ? 'custom' : selectedDate}
              onChange={handleDateChange}
              style={{
                padding: '8px 16px',
                border: '1px solid rgba(191, 10, 48, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#ffffff',
                background: 'rgba(62, 39, 35, 0.8)',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="all">Toutes les dates</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="custom">Date spécifique</option>
            </select>
            {(typeof selectedDate === 'string' && selectedDate.match(/^\d{4}-\d{2}-\d{2}$/)) && (
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid rgba(191, 10, 48, 0.3)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#ffffff',
                  background: 'rgba(62, 39, 35, 0.8)',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              />
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select
              value={selectedPOS}
              onChange={(e) => setSelectedPOS(e.target.value)}
              style={{
                padding: '8px 16px',
                border: '1px solid rgba(191, 10, 48, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#ffffff',
                background: 'rgba(62, 39, 35, 0.8)',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="all">Tous les points de vente</option>
              {pdvList.map(pdv => (
                <option key={pdv} value={pdv}>{pdv}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* KPI Section */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '24px',
        marginBottom: '24px'
      }}>
        <KPICard
          title="Participants"
          value={stats?.summary?.total_participations || 0}
          icon="fa-user-group"
          color="#1890ff"
        />
        <KPICard
          title="Gagnants"
          value={stats?.summary?.total_winners || 0}
          icon="fa-medal"
          color="#52c41a"
        />
        <KPICard
          title="Lots Gagnés"
          value={stats?.summary?.total_prizes || 0}
          icon="fa-gift"
          color="#faad14"
        />
        <KPICard
          title="Bouteilles Vendues"
          value={stats?.summary?.total_bottles || 0}
          icon="fa-wine-bottle"
          color="#eb2f96"
        />
        <KPICard
          title="Points de Vente Actifs"
          value={stats?.summary?.active_points || 0}
          icon="fa-store"
          color="#722ed1"
        />
      </div>

      {/* Two Column Section */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        <PrizeDistributionSection stats={stats} />
        <ParticipationModeSection stats={stats} />
      </div>

      {/* Performance Section */}
      <PerformanceSection stats={stats} />

      {/* Commercial Pressure Section */}
      <CommercialPressureSection stats={stats} hourlyData={hourlyData} message={message} setMessage={setMessage} />
    </div>
  );
};

const KPICard = ({ title, value, icon, color }) => (
  <div style={{
    background: 'rgba(62, 39, 35, 0.9)',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    transition: 'all 0.3s',
    border: '2px solid rgba(191, 10, 48, 0.3)'
  }}
  onMouseEnter={(e) => {
    e.target.style.transform = 'translateY(-2px)';
    e.target.style.boxShadow = '0 4px 12px rgba(191, 10, 48, 0.2)';
    e.target.style.borderColor = 'rgba(191, 10, 48, 0.5)';
  }}
  onMouseLeave={(e) => {
    e.target.style.transform = 'translateY(0)';
    e.target.style.boxShadow = '0 1px 2px rgba(0,0,0,0.03)';
    e.target.style.borderColor = 'rgba(191, 10, 48, 0.3)';
  }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
      <div style={{
        width: '52px',
        height: '52px',
        background: color + '25',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <i className={`fa-solid ${icon}`} style={{ color: color, fontSize: '24px' }}></i>
      </div>
    </div>
    <div style={{ fontSize: '28px', fontWeight: 700, color: '#ffffff', marginBottom: '8px', lineHeight: 1.2 }}>
      {value.toLocaleString()}
    </div>
    <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', fontWeight: 500 }}>
      {title}
    </div>
  </div>
);

const PrizeDistributionSection = ({ stats }) => (
  <div style={{
    background: 'rgba(62, 39, 35, 0.9)',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    border: '2px solid rgba(191, 10, 48, 0.3)'
  }}>
    <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        width: '40px',
        height: '40px',
        background: 'rgba(114, 46, 209, 0.25)',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <i className="fa-solid fa-gift" style={{ color: '#722ed1', fontSize: '18px' }}></i>
      </div>
      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>
        Répartition des Lots
      </h3>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {stats?.prizes && Object.entries(stats.prizes).map(([prizeType, data], idx) => {
        const total = data.won;
        return (
          <div key={prizeType} style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            background: 'rgba(62, 39, 35, 0.5)',
            borderRadius: '12px',
            border: '1px solid rgba(191, 10, 48, 0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '44px',
                height: '44px',
                background: getColor(idx) + '25',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <i className="fa-solid fa-medal" style={{ color: getColor(idx), fontSize: '20px' }}></i>
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#ffffff' }}>{prizeType}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '20px', fontWeight: 700, color: getColor(idx) }}>{total}</div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const ParticipationModeSection = ({ stats }) => (
  <div style={{
    background: 'rgba(62, 39, 35, 0.9)',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    border: '2px solid rgba(191, 10, 48, 0.3)'
  }}>
    <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        width: '40px',
        height: '40px',
        background: 'rgba(24, 144, 255, 0.25)',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <i className="fa-solid fa-share-nodes" style={{ color: '#1890ff', fontSize: '18px' }}></i>
      </div>
      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>
        Mode de Participation
      </h3>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <ModeCard
        icon="fab fa-whatsapp"
        label="WhatsApp"
        value={stats?.engagement?.whatsapp || 0}
        rate={stats?.engagement?.whatsapp_rate || 0}
        color="#25D366"
      />
      <ModeCard
        icon="fa-solid fa-mobile-screen"
        label="Direct"
        value={stats?.engagement?.direct || 0}
        rate={stats?.engagement?.direct_rate || 0}
        color="#1890ff"
      />
      <ModeCard
        icon="fa-solid fa-qrcode"
        label="QR Code"
        value={stats?.engagement?.qr_code || 0}
        rate={stats?.engagement?.qr_code_rate || 0}
        color="#FF6B35"
      />
    </div>
  </div>
);

const ModeCard = ({ icon, label, value, rate, color }) => (
  <div style={{
    padding: '16px',
    background: 'rgba(62, 39, 35, 0.5)',
    borderRadius: '12px',
    border: '1px solid rgba(191, 10, 48, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        width: '44px',
        height: '44px',
        background: color + '25',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <i className={icon} style={{ color: color, fontSize: '22px' }}></i>
      </div>
      <div>
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#ffffff' }}>{label}</div>
        <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginTop: '2px' }}>
          {rate}% du total
        </div>
      </div>
    </div>
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: '20px', fontWeight: 700, color: color }}>{value}</div>
      <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>Participations</div>
    </div>
  </div>
);

const PerformanceSection = ({ stats }) => (
  <div style={{
    background: 'rgba(62, 39, 35, 0.9)',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    border: '2px solid rgba(191, 10, 48, 0.3)'
  }}>
    <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        width: '40px',
        height: '40px',
        background: 'rgba(250, 173, 20, 0.25)',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <i className="fa-solid fa-store" style={{ color: '#faad14', fontSize: '18px' }}></i>
      </div>
      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>
        Performance par Point de Vente
      </h3>
    </div>
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid rgba(191, 10, 48, 0.3)' }}>
            <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Point de Vente</th>
            <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Ville</th>
            <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Inscrits</th>
            <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Gagnants</th>
            <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Perdants</th>
            <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Bouteilles</th>
            <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Taux de gagnants</th>
          </tr>
        </thead>
        <tbody>
          {stats?.by_pos && stats.by_pos.map((pos, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid rgba(191, 10, 48, 0.2)' }}>
              <td style={{ padding: '16px', color: '#ffffff', fontSize: '14px', fontWeight: 500 }}>{pos.name}</td>
              <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>{pos.city}</td>
              <td style={{ padding: '16px', textAlign: 'center', color: '#1890ff', fontSize: '14px', fontWeight: 600 }}>{pos.inscrits || 0}</td>
              <td style={{ padding: '16px', textAlign: 'center', color: '#52c41a', fontSize: '14px', fontWeight: 600 }}>{pos.gagnants || 0}</td>
              <td style={{ padding: '16px', textAlign: 'center', color: '#ff7875', fontSize: '14px', fontWeight: 600 }}>{pos.perdants || 0}</td>
              <td style={{ padding: '16px', textAlign: 'center', color: '#faad14', fontSize: '14px', fontWeight: 600 }}>{pos.bouteilles || 0}</td>
              <td style={{ padding: '16px', textAlign: 'center' }}>
                <span style={{
                  padding: '6px 16px',
                  borderRadius: '20px',
                  background: pos.performance >= 80 ? 'rgba(82, 196, 26, 0.2)' : pos.performance >= 50 ? 'rgba(250, 173, 20, 0.2)' : 'rgba(255, 77, 79, 0.2)',
                  color: pos.performance >= 80 ? '#95de64' : pos.performance >= 50 ? '#FFD700' : '#ff7875',
                  fontWeight: 600,
                  fontSize: '14px',
                  display: 'inline-block'
                }}>
                  {pos.performance || 0}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const CommercialPressureSection = ({ stats, hourlyData, message, setMessage }) => {
  const [selectedPDV, setSelectedPDV] = useState('all');
  const [selectedDisplay, setSelectedDisplay] = useState('all');
  const [hoveredCell, setHoveredCell] = useState(null);

  // Get all unique dates from hourly data
  const getAllDates = () => {
    if (!hourlyData) return [];

    // Priorité: liste déjà calculée côté API (ne contient pas les dates à 0)
    if (Array.isArray(hourlyData.all_dates) && hourlyData.all_dates.length > 0) {
      return [...hourlyData.all_dates].sort().reverse();
    }

    // Fallback: dates présentes dans les données
    if (!hourlyData.by_pdv) return [];
    const dates = new Set();
    hourlyData.by_pdv.forEach(pdv => {
      Object.keys(pdv.dates || {}).forEach(date => dates.add(date));
    });
    return Array.from(dates).sort().reverse();
  };

  // Get all PDVs
  const getAllPDVs = () => {
    if (!hourlyData || !hourlyData.by_pdv) return [];
    return hourlyData.by_pdv.map(pdv => ({
      id: pdv.pdv_id,
      name: pdv.pdv_name
    }));
  };

  // Filter data based on selections
  const getFilteredData = () => {
    if (!hourlyData || !hourlyData.by_pdv) return { dates: [], pdvs: [] };

    let pdvs = hourlyData.by_pdv;

    if (selectedPDV !== 'all') {
      pdvs = pdvs.filter(pdv => pdv.pdv_id === parseInt(selectedPDV));
    }

    const hourFilter = (hour) => {
      if (selectedDisplay === 'all') return true;
      const selectedHour = parseInt(selectedDisplay);
      return !isNaN(selectedHour) && hour === selectedHour;
    };

    const dates = getAllDates();

    return {
      dates,
      pdvs: pdvs.map(pdv => {
        const pdvData = {
          id: pdv.pdv_id,
          name: pdv.pdv_name,
          totals: {},
          hourlyBreakdown: {}
        };

        dates.forEach(date => {
          const dateData = (pdv.dates || {})[date];
          if (dateData && dateData.hourly) {
            let total = 0;
            dateData.hourly.forEach(h => {
              if (hourFilter(h.hour)) {
                total += h.bottles || 0;
              }
            });
            pdvData.totals[date] = total;
            pdvData.hourlyBreakdown[date] = dateData.hourly.filter(h => hourFilter(h.hour));
          } else {
            pdvData.totals[date] = 0;
            pdvData.hourlyBreakdown[date] = [];
          }
        });

        pdvData.total = Object.values(pdvData.totals).reduce((sum, val) => sum + val, 0);

        return pdvData;
      })
    };
  };

  const filteredData = getFilteredData();
  const allPDVs = getAllPDVs();
  const allDates = filteredData.dates;

  const getColumnTotals = () => {
    const totals = {};
    allDates.forEach(date => {
      totals[date] = filteredData.pdvs.reduce((sum, pdv) => sum + (pdv.totals[date] || 0), 0);
    });
    return totals;
  };

  const columnTotals = getColumnTotals();
  const grandTotal = Object.values(columnTotals).reduce((sum, val) => sum + val, 0);

  // Get hourly breakdown for tooltip
  const getHourlyBreakdown = (pdv, date) => {
    const breakdown = pdv.hourlyBreakdown[date] || [];
    if (breakdown.length === 0) return 'Aucune vente';

    const hours = breakdown
      .filter(h => h.bottles > 0)
      .map(h => `${String(h.hour).padStart(2, '0')}h : ${h.bottles}`);

    return hours.length > 0 ? hours.join(' | ') : 'Aucune vente';
  };

  return (
    <div style={{
      background: 'rgba(62, 39, 35, 0.9)',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
      border: '2px solid rgba(191, 10, 48, 0.3)'
    }}>
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '40px',
          height: '40px',
          background: 'rgba(19, 194, 194, 0.25)',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <i className="fa-solid fa-chart-line" style={{ color: '#13c2c2', fontSize: '18px' }}></i>
        </div>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>
          Pression Commerciale
        </h3>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ color: '#bf0a30', fontSize: '15px', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <i className="fa-solid fa-calendar-days"></i>
          Bouteilles vendues par date et par point de vente
        </h4>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>Affichage :</label>
            <select
              value={selectedDisplay}
              onChange={(e) => setSelectedDisplay(e.target.value)}
              style={{
                padding: '8px 12px',
                background: 'rgba(62, 39, 35, 0.8)',
                border: '1px solid rgba(191, 10, 48, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              <option value="all">Toutes les heures</option>
              {Array.from({ length: 14 }, (_, i) => i + 8).map(hour => (
                <option key={hour} value={hour.toString()}>{hour}h</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>Point de vente</label>
            <select
              value={selectedPDV}
              onChange={(e) => setSelectedPDV(e.target.value)}
              style={{
                padding: '8px 12px',
                background: 'rgba(62, 39, 35, 0.8)',
                border: '1px solid rgba(191, 10, 48, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '13px',
                minWidth: '150px',
                cursor: 'pointer'
              }}
            >
              <option value="all">Tous les points de vente</option>
              {allPDVs.map(pdv => (
                <option key={pdv.id} value={pdv.id}>{pdv.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        {allDates.length === 0 || filteredData.pdvs.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)' }}>
            Données non disponibles
          </div>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid rgba(191, 10, 48, 0.2)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid rgba(191, 10, 48, 0.5)' }}>
                  <th style={{
                    padding: '14px 12px',
                    textAlign: 'left',
                    background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)',
                    color: '#fff',
                    position: 'sticky',
                    left: 0,
                    zIndex: 10,
                    fontWeight: 600,
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Point de vente
                  </th>
                  {allDates.map(date => {
                    const dateObj = new Date(date);
                    const isToday = dateObj.toDateString() === new Date().toDateString();
                    const day = String(dateObj.getDate()).padStart(2, '0');
                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const weekday = dateObj.toLocaleDateString('fr-FR', { weekday: 'short' });
                    return (
                      <th key={date} style={{
                        padding: '14px 8px',
                        textAlign: 'center',
                        background: isToday
                          ? 'linear-gradient(135deg, #ff6b6b 0%, #bf0a30 100%)'
                          : 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)',
                        color: '#fff',
                        minWidth: '60px',
                        fontWeight: 600,
                        fontSize: '13px',
                        border: isToday ? '2px solid #ffd700' : 'none',
                        boxShadow: isToday ? '0 0 10px rgba(255, 215, 0, 0.5)' : 'none'
                      }}>
                        <div style={{ fontSize: '15px', fontWeight: 600 }}>
                          {day}/{month}
                        </div>
                        <div style={{ fontSize: '11px', fontWeight: 400, opacity: 0.9 }}>
                          {weekday}
                        </div>
                      </th>
                    );
                  })}
                  <th style={{
                    padding: '14px 12px',
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '13px'
                  }}>
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData.pdvs.map(pdv => (
                  <tr key={pdv.id} style={{
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    transition: 'background 0.2s ease'
                  }}>
                    <td style={{
                      padding: '14px 12px',
                      color: '#fff',
                      position: 'sticky',
                      left: 0,
                      background: '#1a1a2e',
                      fontWeight: 500,
                      fontSize: '13px',
                      borderRight: '1px solid rgba(191, 10, 48, 0.2)'
                    }}>
                      {pdv.name}
                    </td>
                    {allDates.map(date => {
                      const value = pdv.totals[date] || 0;
                      const isToday = new Date(date).toDateString() === new Date().toDateString();
                      const cellKey = `${pdv.id}-${date}`;
                      return (
                        <td
                          key={date}
                          onMouseEnter={() => setHoveredCell(cellKey)}
                          onMouseLeave={() => setHoveredCell(null)}
                          style={{
                            padding: '12px 8px',
                            textAlign: 'center',
                            color: value > 0 ? '#fff' : 'rgba(255, 255, 255, 0.4)',
                            fontWeight: value > 0 ? 600 : 400,
                            fontSize: '14px',
                            background: hoveredCell === cellKey ? 'rgba(191, 10, 48, 0.3)' : 'transparent',
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            cursor: value > 0 ? 'help' : 'default',
                            borderRadius: '4px'
                          }}
                        >
                          <span style={{
                            display: 'inline-block',
                            padding: value > 0 ? '4px 8px' : '4px',
                            borderRadius: '4px',
                            background: value > 0
                              ? `linear-gradient(135deg, #bf0a30 0%, #ff6b6b 100%)`
                              : 'transparent',
                            color: '#fff',
                            boxShadow: value > 0 ? '0 2px 8px rgba(191, 10, 48, 0.4)' : 'none',
                            transform: hoveredCell === cellKey ? 'scale(1.1)' : 'scale(1)',
                            transition: 'all 0.2s ease'
                          }}>
                            {value}
                          </span>
                        </td>
                      );
                    })}
                    <td style={{
                      padding: '14px 12px',
                      textAlign: 'center',
                      color: '#ffd700',
                      fontWeight: 700,
                      fontSize: '15px',
                      textShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
                    }}>
                      {pdv.total}
                    </td>
                  </tr>
                ))}
                {/* Totals row */}
                <tr style={{
                  borderBottom: '2px solid rgba(191, 10, 48, 0.5)',
                  background: 'linear-gradient(90deg, rgba(191, 10, 48, 0.15) 0%, rgba(191, 10, 48, 0.05) 100%)'
                }}>
                  <td style={{
                    padding: '14px 12px',
                    color: '#ffd700',
                    fontWeight: 700,
                    position: 'sticky',
                    left: 0,
                    background: 'linear-gradient(90deg, rgba(191, 10, 48, 0.15) 0%, rgba(191, 10, 48, 0.05) 100%)',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    borderRight: '1px solid rgba(191, 10, 48, 0.3)'
                  }}>
                    Total
                  </td>
                  {allDates.map(date => {
                    const total = columnTotals[date] || 0;
                    const isToday = new Date(date).toDateString() === new Date().toDateString();
                    return (
                      <td key={date} style={{
                        padding: '14px 8px',
                        textAlign: 'center',
                        color: isToday ? '#ffd700' : '#ff6b6b',
                        fontWeight: 700,
                        fontSize: '15px',
                        textShadow: isToday ? '0 0 15px rgba(255, 215, 0, 0.6)' : '0 0 10px rgba(255, 107, 107, 0.4)'
                      }}>
                        {total}
                      </td>
                    );
                  })}
                  <td style={{
                    padding: '14px 12px',
                    textAlign: 'center',
                    color: '#ffd700',
                    fontWeight: 800,
                    fontSize: '16px',
                    textShadow: '0 0 15px rgba(255, 215, 0, 0.6)'
                  }}>
                    {grandTotal}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Tooltip */}
        {hoveredCell && (() => {
          const [pdvId, dateStr] = hoveredCell.split('-');
          const pdv = filteredData.pdvs.find(p => p.id === parseInt(pdvId));
          if (!pdv) return null;

          const dateObj = new Date(dateStr);
          const day = String(dateObj.getDate()).padStart(2, '0');
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const weekday = dateObj.toLocaleDateString('fr-FR', { weekday: 'long' });

          return (
            <div key={hoveredCell} style={{
              position: 'fixed',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'rgba(26, 26, 46, 0.98)',
              border: '1px solid rgba(191, 10, 48, 0.5)',
              borderRadius: '8px',
              padding: '16px',
              zIndex: 1000,
              minWidth: '200px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
              pointerEvents: 'none'
            }}>
              <div style={{ color: '#bf0a30', fontWeight: 600, marginBottom: '8px', fontSize: '14px' }}>
                {pdv.name}
              </div>
              <div style={{ color: '#ffd700', fontWeight: 500, marginBottom: '4px', fontSize: '12px' }}>
                {day}/{month} - {weekday}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px', lineHeight: 1.6 }}>
                {getHourlyBreakdown(pdv, dateStr)}
              </div>
            </div>
          );
        })()}
      </div>

    <div>
      <h4 style={{ color: '#bf0a30', fontSize: '15px', fontWeight: 600, marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <i className="fa-solid fa-warehouse"></i>
        État des réserves (Injections vs Gagnés)
      </h4>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(191, 10, 48, 0.3)' }}>
              <th style={{ padding: '12px', textAlign: 'left', color: '#bf0a30' }}>Point de vente</th>
              <th style={{ padding: '12px', textAlign: 'center', color: '#bf0a30' }}>Injections</th>
              <th style={{ padding: '12px', textAlign: 'center', color: '#bf0a30' }}>Gagnés</th>
              <th style={{ padding: '12px', textAlign: 'center', color: '#bf0a30' }}>En attente</th>
            </tr>
          </thead>
          <tbody>
            {stats?.injection_stats && stats.injection_stats.length > 0 ? (
              stats.injection_stats.map((data) => (
                <tr key={data.pdv_id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <td style={{ padding: '12px', color: '#fff' }}>{data.pdv_name}</td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#fff' }}>{data.injected}</td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#52c41a' }}>{data.won}</td>
                  <td style={{ padding: '12px', textAlign: 'center', color: data.pending > 5 ? '#ff4d4f' : '#faad14' }}>{data.pending}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)' }}>Aucune injection programmée</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>
  );
};

const getColor = (idx) => {
  const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#eb2f96', '#13c2c2'];
  return colors[idx % colors.length];
};

// ============ SESSIONS ============
const SessionsContent = ({ message, setMessage }) => {
  const [sessions, setSessions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/sessions', { params: { brand: BRAND } });
      setSessions(response.data);
    } catch (error) {
      console.error('Error loading sessions:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des sessions' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = () => {
    setSelectedSession(null);
    setShowModal(true);
  };

  const handleToggleSession = async (sessionId) => {
    const session = sessions.find(s => s.id === sessionId);
    const newStatus = session.status === 'active' ? 'pending' : 'active';

    try {
      if (newStatus === 'active') {
        await api.put(`/admin/sessions/${sessionId}/activate`, {}, { params: { brand: BRAND } });
      } else {
        await api.put(`/admin/sessions/${sessionId}/deactivate`, {}, { params: { brand: BRAND } });
      }
      setSessions(sessions.map(s => s.id === sessionId ? { ...s, status: newStatus } : s));
      setMessage({ type: 'success', text: newStatus === 'active' ? 'Session activée' : 'Session désactivée' });
    } catch (error) {
      console.error('Error toggling session:', error);
      setMessage({ type: 'error', text: 'Erreur lors du changement de statut' });
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette session ?')) {
      return;
    }

    try {
      await api.delete(`/admin/sessions/${sessionId}`, { params: { brand: BRAND } });
      setSessions(sessions.filter(s => s.id !== sessionId));
      setMessage({ type: 'success', text: 'Session supprimée avec succès' });
    } catch (error) {
      console.error('Error deleting session:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la suppression' });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'En cours', color: '#95de64', bg: 'rgba(82, 196, 26, 0.2)' },
      completed: { label: 'Terminée', color: 'rgba(255, 255, 255, 0.7)', bg: 'rgba(255, 255, 255, 0.1)' },
      pending: { label: 'Planifiée', color: '#bf0a30', bg: 'rgba(191, 10, 48, 0.2)' },
      cancelled: { label: 'Annulée', color: '#ff4d4f', bg: 'rgba(255, 77, 79, 0.2)' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span style={{
        padding: '6px 16px',
        borderRadius: '20px',
        background: config.bg,
        color: config.color,
        fontSize: '13px',
        fontWeight: 600
      }}>
        {config.label}
      </span>
    );
  };

  const getGainModeBadge = (mode) => {
    if (mode === 'whatsapp') {
      return <span style={{ padding: '4px 12px', borderRadius: '12px', background: '#e8f5e8', color: '#25d366', fontSize: '11px', fontWeight: 600 }}>
        <i className="fab fa-whatsapp" style={{ marginRight: '4px' }}></i>
        WhatsApp
      </span>;
    }
    return <span style={{ padding: '4px 12px', borderRadius: '12px', background: '#e3f2fd', color: '#2196f3', fontSize: '11px', fontWeight: 600 }}>
      <i className="fas fa-mobile-alt" style={{ marginRight: '4px' }}></i>
      Direct
    </span>;
  };

  const SessionKPICard = ({ title, value, icon, color }) => (
    <div style={{
      background: '#ffffff',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
      border: '1px solid #f0f0f0',
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    }}>
      <div style={{
        width: '52px',
        height: '52px',
        background: color + '15',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <i className={`fa-solid ${icon}`} style={{ color: color, fontSize: '24px' }}></i>
      </div>
      <div>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{title}</div>
        <div style={{ fontSize: '28px', fontWeight: 700, color: '#ffffff', lineHeight: 1.2 }}>
          {value.toLocaleString()}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ flex: 1, padding: '30px', textAlign: 'center', color: '#fff' }}>
        Chargement...
      </div>
    );
  }

  return (
    <div style={{ flex: 1, padding: '32px', background: 'transparent', overflowY: 'auto' }}>
      {message.text && (
        <div style={{
          padding: '12px 24px',
          background: message.type === 'error' ? 'rgba(255, 77, 79, 0.15)' : 'rgba(82, 196, 26, 0.15)',
          border: `1px solid ${message.type === 'error' ? 'rgba(255, 77, 79, 0.4)' : 'rgba(82, 196, 26, 0.4)'}`,
          borderRadius: '8px',
          marginBottom: '24px',
          color: message.type === 'error' ? '#ff7875' : '#95de64',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <i className={`fas fa-${message.type === 'error' ? 'exclamation-circle' : 'check-circle'}`}></i>
          {message.text}
        </div>
      )}

      {/* Header */}
      <div style={{
        background: 'rgba(62, 39, 35, 0.9)',
        borderRadius: '16px',
        padding: '24px 32px',
        marginBottom: '24px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: '2px solid rgba(191, 10, 48, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="fas fa-calendar-alt" style={{ color: '#ffffff', fontSize: '20px' }}></i>
          </div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#ffffff' }}>
            Sessions
          </h1>
        </div>

        <button
          onClick={handleCreateSession}
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s',
            boxShadow: '0 2px 8px rgba(191, 10, 48, 0.2)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 12px rgba(191, 10, 48, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 8px rgba(191, 10, 48, 0.2)';
          }}
        >
          <i className="fas fa-plus"></i>
          Nouvelle Session
        </button>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', marginBottom: '24px' }}>
        <SessionKPICard
          title="Total Sessions"
          value={sessions.length}
          icon="fa-calendar-check"
          color="#bf0a30"
        />
        <SessionKPICard
          title="Sessions Actives"
          value={sessions.filter(s => s.status === 'active').length}
          icon="fa-play-circle"
          color="#52c41a"
        />
        <SessionKPICard
          title="Total Participants"
          value={sessions.reduce((acc, s) => acc + (s.stock_allocations?.reduce((a, alloc) => a + (alloc.initial_quantity - alloc.remaining_quantity), 0) || 0), 0)}
          icon="fa-users"
          color="#faad14"
        />
        <SessionKPICard
          title="Total Gagnants"
          value={sessions.reduce((acc, s) => acc + (s.stock_allocations?.reduce((a, alloc) => a + (alloc.initial_quantity - alloc.remaining_quantity), 0) || 0), 0)}
          icon="fa-trophy"
          color="#722ed1"
        />
      </div>

      {/* Filters */}
      <div style={{
        background: 'rgba(62, 39, 35, 0.9)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        border: '2px solid rgba(191, 10, 48, 0.3)',
        marginBottom: '24px'
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '14px', fontWeight: 600, color: '#bf0a30', textTransform: 'uppercase' }}>
          Filtres
        </h3>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fas fa-store" style={{ color: '#bf0a30', fontSize: '14px' }}></i>
            <select
              defaultValue="all"
              style={{
                padding: '8px 16px',
                border: '1px solid rgba(191, 10, 48, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#ffffff',
                background: 'rgba(62, 39, 35, 0.8)',
                cursor: 'pointer'
              }}
            >
              <option value="all">Tous les points de vente</option>
              {[...new Set(sessions.map(s => s.pdv_name || "Non assigné"))].map(pos => (
                <option key={pos} value={pos}>{pos}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fas fa-info-circle" style={{ color: '#bf0a30', fontSize: '14px' }}></i>
            <select
              defaultValue="all"
              style={{
                padding: '8px 16px',
                border: '1px solid rgba(191, 10, 48, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#ffffff',
                background: 'rgba(62, 39, 35, 0.8)',
                cursor: 'pointer'
              }}
            >
              <option value="all">Tous les statuts</option>
              <option value="active">En cours</option>
              <option value="completed">Terminée</option>
              <option value="pending">Planifiée</option>
              <option value="cancelled">Annulée</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fas fa-calendar" style={{ color: '#bf0a30', fontSize: '14px' }}></i>
            <input
              type="date"
              style={{
                padding: '8px 16px',
                border: '1px solid rgba(191, 10, 48, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#ffffff',
                background: 'rgba(62, 39, 35, 0.8)',
                cursor: 'pointer'
              }}
            />
          </div>
        </div>
      </div>

      {/* Sessions Table */}
      <div style={{
        background: 'rgba(62, 39, 35, 0.9)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        border: '2px solid rgba(191, 10, 48, 0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: '#bf0a30',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="fa-solid fa-list" style={{ color: '#fff', fontSize: '18px' }}></i>
            </div>
            Liste des Sessions ({sessions.length})
          </h3>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid rgba(191, 10, 48, 0.3)' }}>
                <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Point de Vente</th>
                <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Session</th>
                <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Date & Horaire</th>
                <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Mode</th>
                <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Participants</th>
                <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Gagnants</th>
                <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Statut</th>
                <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr key={session.id} style={{ borderBottom: '1px solid rgba(191, 10, 48, 0.2)' }}>
                  <td style={{ padding: '16px', color: '#ffffff', fontSize: '14px', fontWeight: 600 }}>{session.pdv_name || session.pdv?.name || 'Non assigné'}</td>
                  <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>{session.name}</td>
                  <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)', fontSize: '13px' }}>
                    {new Date(session.start_time).toLocaleDateString('fr-FR')} {new Date(session.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - {new Date(session.end_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    {getGainModeBadge(session.gain_mode)}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center', color: '#bf0a30', fontSize: '14px', fontWeight: 600 }}>{session.stock_allocations?.reduce((a, alloc) => a + (alloc.initial_quantity - alloc.remaining_quantity), 0) || 0}</td>
                  <td style={{ padding: '16px', textAlign: 'center', color: '#bf0a30', fontSize: '14px', fontWeight: 600 }}>{session.stock_allocations?.reduce((a, alloc) => a + (alloc.initial_quantity - alloc.remaining_quantity), 0) || 0}</td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>{getStatusBadge(session.status)}</td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                      <button
                        onClick={() => handleToggleSession(session.id)}
                        style={{
                          padding: '6px 12px',
                          background: session.status === "active" ? 'rgba(82, 196, 26, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                          color: session.status === "active" ? '#52c41a' : '#ff4d4f',
                          border: `1px solid ${session.status === "active" ? '#52c41a' : '#ff4d4f'}`,
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 600,
                          transition: 'all 0.2s'
                        }}
                      >
                        <i className={`fas fa-${session.status === "active" ? 'toggle-on' : 'toggle-off'}`}></i>
                        {session.status === "active" ? 'Activé' : 'Désactivé'}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedSession(session);
                          setShowModal(true);
                        }}
                        style={{
                          padding: '6px 12px',
                          background: '#bf0a30',
                          color: '#ffffff',
                          border: '1px solid #bf0a30',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 600,
                          transition: 'all 0.2s'
                        }}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        onClick={() => handleDeleteSession(session.id)}
                        style={{
                          padding: '6px 12px',
                          background: 'rgba(255, 77, 79, 0.1)',
                          color: '#ff4d4f',
                          border: '1px solid rgba(255, 77, 79, 0.4)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 600,
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#ff4d4f';
                          e.target.style.color = '#fff';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(255, 77, 79, 0.1)';
                          e.target.style.color = '#ff4d4f';
                        }}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <SessionModal
          session={selectedSession}
          onClose={() => setShowModal(false)}
          onSave={async () => {
            await loadSessions();
            setShowModal(false);
          }}
        />
      )}
    </div>
  );
};

const SessionModal = ({ session, onClose, onSave }) => {
  const [pdvs, setPdvs] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [formData, setFormData] = useState({
    name: session?.name || '',
    pdv_id: session?.pdv_id || '',
    start_time: session?.start_time ? new Date(session.start_time).toISOString().slice(0, 16) : '',
    end_time: session?.end_time ? new Date(session.end_time).toISOString().slice(0, 16) : '',
    gain_mode: session?.gain_mode || 'auto',
    ratio_value: session?.ratio_value || 5,
    percentage_value: session?.percentage_value || 80,
    stock_allocations: session?.stock_allocations || {}
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pdvsRes, prizesRes] = await Promise.all([
        api.get('/admin/pdv', { params: { brand: BRAND } }),
        api.get('/admin/prizes', { params: { brand: BRAND } })
      ]);
      setPdvs(pdvsRes.data);
      setPrizes(prizesRes.data);

      if (!session && Object.keys(formData.stock_allocations).length === 0) {
        const initialStock = {};
        prizesRes.data.forEach(p => {
          initialStock[p.id] = p.prize_type.includes('try_again') ? 999 : 10;
        });
        setFormData({ ...formData, stock_allocations: initialStock });
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const stock_allocations = Object.entries(formData.stock_allocations).map(([prizeId, quantity]) => ({
      prize_config_id: parseInt(prizeId),
      initial_quantity: parseInt(quantity)
    }));

    const sessionData = {
      name: formData.name,
      pdv_id: parseInt(formData.pdv_id),
      start_time: new Date(formData.start_time).toISOString(),
      end_time: new Date(formData.end_time).toISOString(),
      gain_mode: formData.gain_mode,
      stock_allocations
    };

    if (formData.gain_mode === 'ratio') {
      sessionData.ratio_value = formData.ratio_value;
    }

    if (formData.gain_mode === 'pourcentage') {
      sessionData.percentage_value = formData.percentage_value;
    }

    try {
      if (session) {
        await api.put(`/admin/sessions/${session.id}`, { ...sessionData, brand: BRAND });
      } else {
        await api.post('/admin/sessions', { ...sessionData, brand: BRAND });
      }
      onSave(sessionData);
    } catch (error) {
      const errorData = error.response?.data;
      let errorMessage = 'Erreur lors de la création de la session';
      if (errorData?.detail) {
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => {
            if (typeof err === 'string') return err;
            if (err.msg) return `${err.loc?.join('.') || 'Champ'}: ${err.msg}`;
            return JSON.stringify(err);
          }).join('\n');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
      }
      alert(errorMessage);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'rgba(62, 39, 35, 0.95)',
        borderRadius: '16px',
        padding: '32px',
        width: '100%',
        maxWidth: '550px',
        maxHeight: '90vh',
        overflowY: 'auto',
        border: '2px solid rgba(191, 10, 48, 0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#ffffff' }}>
            {session ? 'Modifier la Session' : 'Nouvelle Session'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
              color: 'rgba(255, 255, 255, 0.7)'
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#bf0a30' }}>
              <i className="fas fa-store" style={{ marginRight: '8px', color: '#bf0a30' }}></i>
              Point de Vente
            </label>
            <select
              value={formData.pdv_id}
              onChange={(e) => setFormData({ ...formData, pdv_id: parseInt(e.target.value) })}
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid rgba(191, 10, 48, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#ffffff',
                background: 'rgba(62, 39, 35, 0.8)',
                outline: 'none'
              }}
            >
              <option value="">Sélectionner un point de vente</option>
              {pdvs.map(pdv => (
                <option key={pdv.id} value={pdv.id}>{pdv.name} ({pdv.city?.name})</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#bf0a30' }}>
              <i className="fas fa-bolt" style={{ marginRight: '8px', color: '#bf0a30' }}></i>
              Nom de la session
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Session Promo - Janvier"
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid rgba(191, 10, 48, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#ffffff',
                background: 'rgba(62, 39, 35, 0.8)',
                outline: 'none'
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#bf0a30' }}>
                <i className="fas fa-play" style={{ marginRight: '8px', color: '#bf0a30' }}></i>
                Début
              </label>
              <input
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid rgba(191, 10, 48, 0.3)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#ffffff',
                  background: 'rgba(62, 39, 35, 0.8)',
                  outline: 'none'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#bf0a30' }}>
                <i className="fas fa-stop" style={{ marginRight: '8px', color: '#bf0a30' }}></i>
                Fin
              </label>
              <input
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid rgba(191, 10, 48, 0.3)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#ffffff',
                  background: 'rgba(62, 39, 35, 0.8)',
                  outline: 'none'
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#bf0a30' }}>
              <i className="fas fa-cog" style={{ marginRight: '8px', color: '#bf0a30' }}></i>
              Mode de gain
            </label>
            <select
              value={formData.gain_mode}
              onChange={(e) => setFormData({ ...formData, gain_mode: e.target.value })}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid rgba(191, 10, 48, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#ffffff',
                background: 'rgba(62, 39, 35, 0.8)',
                outline: 'none'
              }}
            >
              <option value="auto">Automatique - Distribution basée sur les quotas</option>
              <option value="ratio">Ratio - 1 gagnant tous les X participants</option>
              <option value="force">Forcé - Instant gagnant programmé (gros lots)</option>
              <option value="pourcentage">Pourcentage - % global de gains basé sur les stocks alloués</option>
            </select>
            {formData.gain_mode === 'ratio' && (
              <div style={{ marginTop: '8px' }}>
                <input
                  type="number"
                  value={formData.ratio_value}
                  onChange={(e) => setFormData({ ...formData, ratio_value: parseInt(e.target.value) })}
                  min="1"
                  placeholder="Ex: 5 = 1 gagnant tous les 5"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid rgba(191, 10, 48, 0.3)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#ffffff',
                    background: 'rgba(62, 39, 35, 0.8)',
                    outline: 'none'
                  }}
                />
              </div>
            )}
            {formData.gain_mode === 'pourcentage' && (
              <div style={{ marginTop: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'rgba(191, 10, 48, 0.8)' }}>
                  Pourcentage global de gains (ex: 80 = 80% des lots alloués seront gagnés)
                </label>
                <input
                  type="number"
                  value={formData.percentage_value}
                  onChange={(e) => setFormData({ ...formData, percentage_value: parseInt(e.target.value) })}
                  min="1"
                  max="100"
                  placeholder="Ex: 80 = 80% de gains"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid rgba(191, 10, 48, 0.3)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#ffffff',
                    background: 'rgba(62, 39, 35, 0.8)',
                    outline: 'none'
                  }}
                />
              </div>
            )}
            {formData.gain_mode === 'force' && (
              <div style={{ marginTop: '8px', padding: '12px', background: 'rgba(191, 10, 48, 0.15)', borderRadius: '8px', border: '1px solid rgba(191, 10, 48, 0.3)' }}>
                <div style={{ fontSize: '12px', color: '#bf0a30', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <i className="fas fa-info-circle"></i>
                  <span>Ce mode permet de programmer des gains forcés via le module Injection pour les gros lots (Amitié, Aventure)</span>
                </div>
              </div>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: 600, color: '#bf0a30' }}>
              <i className="fas fa-boxes" style={{ marginRight: '8px', color: '#bf0a30' }}></i>
              Allocation des Stocks
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              {prizes.filter(p => !p.prize_type.includes('try_again')).map(prize => (
                <div key={prize.id} style={{ border: '1px solid rgba(191, 10, 48, 0.3)', borderRadius: '8px', padding: '12px', background: 'rgba(62, 39, 35, 0.5)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      background: prize.color,
                      borderRadius: '50%'
                    }}></div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#ffffff' }}>
                      {prize.name}
                      <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.6)' }}>{prize.prize_label}</div>
                    </div>
                  </div>
                  <input
                    type="number"
                    value={formData.stock_allocations[prize.id] || 0}
                    onChange={(e) => setFormData({
                      ...formData,
                      stock_allocations: { ...formData.stock_allocations, [prize.id]: parseInt(e.target.value) || 0 }
                    })}
                    min="0"
                    required
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid rgba(191, 10, 48, 0.3)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      color: '#ffffff',
                      background: 'rgba(62, 39, 35, 0.8)',
                      outline: 'none'
                    }}
                  />
                </div>
              ))}
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginTop: '8px' }}>
              * Les Try Again sont illimités et n'ont pas besoin de stock
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px 24px',
                background: 'transparent',
                color: '#bf0a30',
                border: '1px solid rgba(191, 10, 48, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              style={{
                flex: 1,
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {session ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============ PDVS ============
const PDVsContent = ({ message, setMessage }) => {
  const [pdvs, setPdvs] = useState([]);
  const [cities, setCities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedPDV, setSelectedPDV] = useState(null);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    city_id: '',
    has_big_screen: false
  });

  useEffect(() => {
    loadPDVs();
    loadCities();
  }, []);

  const loadPDVs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/pdv', { params: { brand: BRAND } });
      setPdvs(response.data);
    } catch (error) {
      console.error('Error loading PDVs:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des points de vente' });
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async () => {
    try {
      const response = await api.get('/admin/cities');
      setCities(response.data);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const handleCreatePDV = () => {
    setSelectedPDV(null);
    setFormData({ name: '', code: '', address: '', city_id: '', has_big_screen: false });
    setShowModal(true);
  };

  const handleEditPDV = (pdv) => {
    setSelectedPDV(pdv);
    setFormData({
      name: pdv.name,
      code: pdv.code,
      address: pdv.address || '',
      city_id: pdv.city_id?.toString() || '',
      has_big_screen: pdv.has_big_screen || false
    });
    setShowModal(true);
  };

  const handleSavePDV = async () => {
    try {
      if (selectedPDV) {
        await api.put(`/admin/pdv/${selectedPDV.id}`, formData);
        setMessage({ type: 'success', text: 'Point de vente mis à jour avec succès' });
      } else {
        await api.post('/admin/pdv', formData);
        setMessage({ type: 'success', text: 'Point de vente créé avec succès' });
      }
      setShowModal(false);
      loadPDVs();
    } catch (error) {
      console.error('Error saving PDV:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la sauvegarde';
      setMessage({ type: 'error', text: errorMsg });
    }
  };

  const handleDeletePDV = async (pdvId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce point de vente ?')) {
      return;
    }

    try {
      await api.delete(`/admin/pdv/${pdvId}`);
      setPdvs(pdvs.filter(p => p.id !== pdvId));
      setMessage({ type: 'success', text: 'Point de vente supprimé avec succès' });
    } catch (error) {
      console.error('Error deleting PDV:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la suppression';
      setMessage({ type: 'error', text: errorMsg });
    }
  };

  if (loading) {
    return (
      <div style={{ flex: 1, padding: '32px', background: 'transparent' }}>
        <div style={{ color: '#ffffff' }}>Chargement...</div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, padding: '32px', background: 'transparent', overflowY: 'auto' }}>
      {message.text && (
        <div style={{
          padding: '12px 24px',
          background: message.type === 'error' ? 'rgba(255, 77, 79, 0.15)' : 'rgba(82, 196, 26, 0.15)',
          border: `1px solid ${message.type === 'error' ? 'rgba(255, 77, 79, 0.4)' : 'rgba(82, 196, 26, 0.4)'}`,
          borderRadius: '8px',
          marginBottom: '24px',
          color: message.type === 'error' ? '#ff7875' : '#95de64',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <i className={`fas fa-${message.type === 'error' ? 'exclamation-circle' : 'check-circle'}`}></i>
          {message.text}
        </div>
      )}

      {/* Header */}
      <div style={{
        background: 'rgba(62, 39, 35, 0.9)',
        borderRadius: '16px',
        padding: '24px 32px',
        marginBottom: '24px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: '2px solid rgba(191, 10, 48, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="fas fa-store" style={{ color: '#ffffff', fontSize: '20px' }}></i>
          </div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#ffffff' }}>
            Points de Vente
          </h1>
        </div>

        <button
          onClick={handleCreatePDV}
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s',
            boxShadow: '0 2px 8px rgba(191, 10, 48, 0.2)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 12px rgba(191, 10, 48, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 8px rgba(191, 10, 48, 0.2)';
          }}
        >
          <i className="fas fa-plus"></i>
          Nouveau PDV
        </button>
      </div>

      {/* Table */}
      <div style={{
        background: 'rgba(62, 39, 35, 0.9)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        border: '2px solid rgba(191, 10, 48, 0.3)'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid rgba(191, 10, 48, 0.3)' }}>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Nom</th>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Code</th>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Ville</th>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Adresse</th>
              <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Statut</th>
              <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pdvs.map((pdv) => (
              <tr key={pdv.id} style={{ borderBottom: '1px solid rgba(191, 10, 48, 0.2)' }}>
                <td style={{ padding: '16px', color: '#ffffff', fontSize: '14px', fontWeight: 600 }}>{pdv.name}</td>
                <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>{pdv.code}</td>
                <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>{pdv.city?.name || '-'}</td>
                <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>{pdv.address || '-'}</td>
                <td style={{ padding: '16px', textAlign: 'center' }}>
                  <span style={{
                    padding: '6px 16px',
                    borderRadius: '20px',
                    background: pdv.is_active ? 'rgba(82, 196, 26, 0.2)' : 'rgba(255, 77, 79, 0.2)',
                    color: pdv.is_active ? '#95de64' : '#ff7875',
                    fontSize: '13px',
                    fontWeight: 600
                  }}>
                    {pdv.is_active ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td style={{ padding: '16px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                    <button
                      onClick={() => handleEditPDV(pdv)}
                      style={{
                        padding: '6px 12px',
                        background: 'rgba(24, 144, 255, 0.15)',
                        color: '#1890ff',
                        border: '1px solid rgba(24, 144, 255, 0.3)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 600,
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#1890ff';
                        e.target.style.color = '#fff';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(24, 144, 255, 0.15)';
                        e.target.style.color = '#1890ff';
                      }}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleDeletePDV(pdv.id)}
                      style={{
                        padding: '6px 12px',
                        background: 'rgba(255, 77, 79, 0.15)',
                        color: '#ff4d4f',
                        border: '1px solid rgba(255, 77, 79, 0.3)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 600,
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#ff4d4f';
                        e.target.style.color = '#fff';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 77, 79, 0.15)';
                        e.target.style.color = '#ff4d4f';
                      }}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'rgba(62, 39, 35, 0.95)',
            borderRadius: '16px',
            padding: '32px',
            width: '90%',
            maxWidth: '500px',
            border: '2px solid rgba(191, 10, 48, 0.3)'
          }}>
            <h2 style={{ color: '#bf0a30', marginBottom: '24px', fontSize: '20px' }}>
              {selectedPDV ? 'Modifier le PDV' : 'Nouveau Point de Vente'}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ color: '#fff', marginBottom: '8px', display: 'block', fontSize: '14px' }}>Nom *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid rgba(191, 10, 48, 0.3)',
                    borderRadius: '8px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ color: '#fff', marginBottom: '8px', display: 'block', fontSize: '14px' }}>Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid rgba(191, 10, 48, 0.3)',
                    borderRadius: '8px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ color: '#fff', marginBottom: '8px', display: 'block', fontSize: '14px' }}>Ville *</label>
                <select
                  value={formData.city_id}
                  onChange={(e) => setFormData({ ...formData, city_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid rgba(191, 10, 48, 0.3)',
                    borderRadius: '8px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Sélectionner une ville</option>
                  {cities.map(city => (
                    <option key={city.id} value={city.id}>{city.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ color: '#fff', marginBottom: '8px', display: 'block', fontSize: '14px' }}>Adresse</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid rgba(191, 10, 48, 0.3)',
                    borderRadius: '8px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={formData.has_big_screen}
                  onChange={(e) => setFormData({ ...formData, has_big_screen: e.target.checked })}
                  style={{ width: '18px', height: '18px' }}
                />
                <label style={{ color: '#fff', fontSize: '14px', margin: 0 }}>Écran géant</label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  color: '#fff',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleSavePDV}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600
                }}
              >
                {selectedPDV ? 'Mettre à jour' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ PRIZES ============
const PrizesContent = ({ message, setMessage }) => {
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPrize, setEditingPrize] = useState(null);

  useEffect(() => {
    loadPrizes();
  }, []);

  const loadPrizes = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/prizes', { params: { brand: BRAND } });
      setPrizes(response.data);
    } catch (error) {
      console.error('Error loading prizes:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des lots' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (prizeData) => {
    try {
      await api.put(`/admin/prizes/${editingPrize.id}`, prizeData);
      setMessage({ type: 'success', text: 'Lot mis à jour avec succès' });
      setEditingPrize(null);
      loadPrizes();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la mise à jour' });
    }
  };

  if (loading) {
    return <div style={{ padding: '32px', textAlign: 'center', color: '#ffffff' }}>Chargement...</div>;
  }

  return (
    <div style={{ flex: 1, padding: '32px', background: 'transparent', overflowY: 'auto' }}>
      {message.text && (
        <div style={{
          padding: '12px 24px',
          background: message.type === 'error' ? 'rgba(255, 77, 79, 0.15)' : 'rgba(82, 196, 26, 0.15)',
          border: `1px solid ${message.type === 'error' ? 'rgba(255, 77, 79, 0.4)' : 'rgba(82, 196, 26, 0.4)'}`,
          borderRadius: '8px',
          marginBottom: '24px',
          color: message.type === 'error' ? '#ff7875' : '#95de64',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <i className={`fas fa-${message.type === 'error' ? 'exclamation-circle' : 'check-circle'}`}></i>
          {message.text}
        </div>
      )}

      <div style={{
        background: 'rgba(62, 39, 35, 0.9)',
        borderRadius: '16px',
        padding: '24px 32px',
        marginBottom: '24px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        border: '2px solid rgba(191, 10, 48, 0.3)'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <i className="fas fa-gift" style={{ color: '#ffffff', fontSize: '18px' }}></i>
        </div>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#ffffff' }}>
          Configuration des Lots
        </h1>
      </div>

      <div style={{
        background: 'rgba(62, 39, 35, 0.9)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        border: '2px solid rgba(191, 10, 48, 0.3)'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid rgba(191, 10, 48, 0.3)' }}>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Segment</th>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Émotion</th>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Lot</th>
              <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Couleur</th>
              <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Injection Admin</th>
              <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {prizes.map((prize, idx) => (
              <tr key={prize.id} style={{ borderBottom: '1px solid rgba(191, 10, 48, 0.2)' }}>
                <td style={{ padding: '16px', fontWeight: 600, color: '#ffffff' }}>{prize.segment_order}</td>
                <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)' }}>{prize.name}</td>
                <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)' }}>{prize.prize_label}</td>
                <td style={{ padding: '16px', textAlign: 'center' }}>
                  <div style={{ width: '30px', height: '30px', background: prize.color, borderRadius: '50%', margin: '0 auto' }}></div>
                </td>
                <td style={{ padding: '16px', textAlign: 'center' }}>
                  {prize.requires_admin_injection ? (
                    <span style={{ padding: '4px 12px', borderRadius: '20px', background: 'rgba(250, 140, 22, 0.2)', color: '#bf0a30', fontSize: '12px', fontWeight: 600 }}>
                      Oui
                    </span>
                  ) : (
                    <span style={{ padding: '4px 12px', borderRadius: '20px', background: 'rgba(191, 10, 48, 0.2)', color: '#bf0a30', fontSize: '12px' }}>
                      Non
                    </span>
                  )}
                </td>
                <td style={{ padding: '16px', textAlign: 'center' }}>
                  <button
                    onClick={() => setEditingPrize(prize)}
                    style={{
                      padding: '6px 16px',
                      background: 'rgba(191, 10, 48, 0.15)',
                      color: '#bf0a30',
                      border: '1px solid rgba(191, 10, 48, 0.3)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 600
                    }}
                  >
                    <i className="fas fa-edit"></i> Modifier
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingPrize && (
        <PrizeEditModal
          prize={editingPrize}
          onClose={() => setEditingPrize(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

const PrizeEditModal = ({ prize, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: prize.name,
    prize_label: prize.prize_label,
    description: prize.description || '',
    color: prize.color,
    icon: prize.icon || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'rgba(62, 39, 35, 0.95)', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', border: '2px solid rgba(191, 10, 48, 0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#ffffff' }}>Modifier le Lot</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', color: 'rgba(255, 255, 255, 0.7)' }}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#fff' }}>Émotion</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              style={{ width: '100%', padding: '12px', border: '1px solid rgba(191, 10, 48, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(255, 255, 255, 0.1)', color: '#ffffff' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#fff' }}>Lot</label>
            <input
              type="text"
              value={formData.prize_label}
              onChange={(e) => setFormData({ ...formData, prize_label: e.target.value })}
              required
              style={{ width: '100%', padding: '12px', border: '1px solid rgba(191, 10, 48, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(255, 255, 255, 0.1)', color: '#ffffff' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#fff' }}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              style={{ width: '100%', padding: '12px', border: '1px solid rgba(191, 10, 48, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(255, 255, 255, 0.1)', color: '#ffffff' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#fff' }}>Couleur</label>
            <input
              type="color"
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              style={{ width: '100%', height: '50px', padding: '8px', border: '1px solid rgba(191, 10, 48, 0.3)', borderRadius: '8px', background: 'rgba(255, 255, 255, 0.1)', color: '#ffffff' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ flex: 1, padding: '12px', background: 'rgba(255, 255, 255, 0.1)', color: '#ffffff', border: '1px solid rgba(191, 10, 48, 0.3)', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
            >
              Annuler
            </button>
            <button
              type="submit"
              style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
            >
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============ INJECTION ============
const InjectionContent = ({ message, setMessage }) => {
  const [sessions, setSessions] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedPrize, setSelectedPrize] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSessions();
    loadPrizes();
  }, []);

  const loadSessions = async () => {
    try {
      const response = await api.get('/admin/sessions', { params: { brand: BRAND } });
      setSessions(response.data);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const loadPrizes = async () => {
    try {
      const response = await api.get('/admin/prizes', { params: { brand: BRAND } });
      setPrizes(response.data);
    } catch (error) {
      console.error('Error loading prizes:', error);
    }
  };

  const handleInject = async () => {
    if (!selectedSession || !selectedPrize) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner une session et un lot' });
      return;
    }

    setLoading(true);
    try {
      await api.post('/admin/inject-win', {
        session_id: parseInt(selectedSession),
        prize_type: selectedPrize,
        brand: BRAND
      });
      setMessage({ type: 'success', text: 'Gain injecté avec succès !' });
      setSelectedSession('');
      setSelectedPrize('');
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de l\'injection' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ flex: 1, padding: '30px', overflowY: 'auto' }}>
      <h2 style={{ color: '#ffffff', marginBottom: '24px', fontSize: '28px', fontWeight: 600 }}>
        💉 Injection de gains forcés
      </h2>

      {message.text && (
        <div style={{
          padding: '14px 18px',
          borderRadius: '8px',
          marginBottom: '20px',
          background: message.type === 'success' ? '#e8f5e8' : '#ffe5e5',
          border: `2px solid ${message.type === 'success' ? '#228b22' : '#bf0a30'}`,
          color: message.type === 'success' ? '#228b22' : '#bf0a30'
        }}>
          {message.text}
        </div>
      )}

      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '12px',
        padding: '24px',
        border: '2px solid rgba(191, 10, 48, 0.3)'
      }}>
        <h3 style={{ color: '#bf0a30', margin: '0 0 20px', fontSize: '18px' }}>Forcer un gain pour le prochain joueur</h3>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontSize: '14px', fontWeight: 600 }}>Session *</label>
          <select
            value={selectedSession}
            onChange={(e) => setSelectedSession(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '8px',
              border: '2px solid #ddd',
              fontSize: '14px'
            }}
          >
            <option value="">Sélectionner une session</option>
            {sessions.map(s => <option key={s.id} value={s.id}>{s.name} - {s.pdv_name}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontSize: '14px', fontWeight: 600 }}>Lot à forcer *</label>
          <select
            value={selectedPrize}
            onChange={(e) => setSelectedPrize(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '8px',
              border: '2px solid #ddd',
              fontSize: '14px'
            }}
          >
            <option value="">Sélectionner un lot</option>
            {prizes.filter(p => p.prize_type !== 'try_again_1' && p.prize_type !== 'try_again_2').map(p => (
              <option key={p.id} value={p.prize_type}>{p.prize_label}</option>
            ))}
          </select>
        </div>

        <button
          onClick={handleInject}
          disabled={loading}
          style={{
            width: '100%',
            padding: '14px',
            background: loading ? 'rgba(191, 10, 48, 0.5)' : '#bf0a30',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Injection...' : '💉 Injecter ce gain'}
        </button>
      </div>
    </div>
  );
};

// ============ USERS ============
const UsersContent = ({ message, setMessage }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/users', { params: { brand: BRAND } });
      setUsers(response.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors du chargement des utilisateurs' });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ flex: 1, padding: '30px', textAlign: 'center', color: '#fff' }}>
        Chargement...
      </div>
    );
  }

  return (
    <div style={{ flex: 1, padding: '30px', overflowY: 'auto' }}>
      <h2 style={{ color: '#ffffff', marginBottom: '24px', fontSize: '28px', fontWeight: 600 }}>
        👥 Utilisateurs
      </h2>

      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '12px',
        border: '2px solid rgba(191, 10, 48, 0.3)',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ padding: '14px', textAlign: 'left', background: '#bf0a30', color: '#fff' }}>Username</th>
              <th style={{ padding: '14px', textAlign: 'left', background: '#bf0a30', color: '#fff' }}>Email</th>
              <th style={{ padding: '14px', textAlign: 'left', background: '#bf0a30', color: '#fff' }}>Rôle</th>
              <th style={{ padding: '14px', textAlign: 'left', background: '#bf0a30', color: '#fff' }}>PDV</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                <td style={{ padding: '14px', fontWeight: 600 }}>{user.username}</td>
                <td style={{ padding: '14px' }}>{user.email || '-'}</td>
                <td style={{ padding: '14px' }}>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    background: '#bf0a30',
                    color: '#fff'
                  }}>
                    {user.role}
                  </span>
                </td>
                <td style={{ padding: '14px' }}>{user.pdv_name || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Dashboard;
